import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Tag, Select, DatePicker, Input, Tooltip, Empty, Pagination, message } from 'antd';
import { ReloadOutlined, SearchOutlined, DownOutlined, UpOutlined } from '@ant-design/icons';
import Boton from '../Components/Atoms/Boton';
import Esqueleto from '../Components/Atoms/Esqueleto';
import clienteAxios from '../Utils/clienteAxios';

const { RangePicker } = DatePicker;

/**
 * Bitácora de movimientos, para el usuario maestro.
 *
 * Responde a tres preguntas: quién está dentro ahora mismo, quién entró y
 * cuándo, y quién tocó los datos de quién.
 *
 * El backend vuelve a comprobar el permiso en cada llamada; lo de aquí solo
 * decide qué se dibuja.
 */

/** Color por familia de movimiento, para poder barrer la tabla con la vista. */
const COLOR_EVENTO = {
  'sesion.inicio': 'blue',
  'sesion.inicio_sso': 'geekblue',
  'sesion.cierre': 'default',
  'sesion.cambio_empresa': 'purple',
  'usuario.creado': 'green',
  'usuario.modificado': 'gold',
  'usuario.eliminado': 'red',
  'usuario.acceso_reenviado': 'cyan',
  'cuentas.vinculadas': 'magenta',
  'cuentas.desvinculadas': 'volcano'
};

const fechaHora = (valor) => {
  if (!valor) return '';
  // Las fechas llegan del backend como "YYYY-MM-DD HH:mm:ss"; se presenta tal
  // cual, sin convertir zonas horarias, para que coincida con lo que ve quien
  // opera el sistema.
  return String(valor).replace('T', ' ').substring(0, 19);
};

const Campo = ({ etiqueta, children }) => (
  <div className="logs-campo">
    <div className="logs-etiqueta">{etiqueta}</div>
    <div className="logs-valor">{children}</div>
  </div>
);
Campo.propTypes = { etiqueta: PropTypes.string, children: PropTypes.node };

const Movimiento = ({ fila }) => {
  const [abierto, setAbierto] = useState(false);
  const cambios = fila.changes && Object.keys(fila.changes).length > 0 ? fila.changes : null;
  return (
    <article className="logs-mov">
      <div className="logs-mov__cabecera">
        <Tag color={COLOR_EVENTO[fila.event] || 'default'}>{fila.event_label}</Tag>
        <span className="logs-mov__fecha">{fechaHora(fila.created_at)}</span>
      </div>
      <div className="logs-mov__campos">
        <Campo etiqueta="Quién">
          {fila.actor_name ? (
            <>
              <div>{fila.actor_name}</div>
              <div className="logs-secundario">{fila.actor_email}</div>
              {fila.authenticated_as_email && (
                <Tooltip title="Operó desde una cuenta vinculada; se autenticó con esta otra">
                  <Tag color="purple">entró como {fila.authenticated_as_email}</Tag>
                </Tooltip>
              )}
            </>
          ) : (
            '—'
          )}
        </Campo>
        <Campo etiqueta="Qué se hizo">
          <div>{fila.description}</div>
          {cambios && (
            <div className="logs-secundario">{Object.keys(cambios).length} dato(s) afectado(s)</div>
          )}
        </Campo>
        <Campo etiqueta="Sobre quién">{fila.target_name || '—'}</Campo>
        <Campo etiqueta="Origen">{fila.ip_address || '—'}</Campo>
      </div>
      {cambios && (
        <div className="logs-mov__cambios">
          <button
            type="button"
            className="logs-ver-cambios"
            aria-expanded={abierto}
            onClick={() => setAbierto(!abierto)}>
            {abierto ? 'Ocultar cambios' : 'Ver cambios'}
            {abierto ? <UpOutlined aria-hidden="true" /> : <DownOutlined aria-hidden="true" />}
          </button>
          {abierto && (
            <table className="tabla-cambios">
              <thead>
                <tr>
                  <th>DATO</th>
                  <th>ANTES</th>
                  <th>DESPUÉS</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(cambios).map(([campo, valores]) => (
                  <tr key={campo}>
                    <td>{campo}</td>
                    <td>
                      {valores && typeof valores === 'object' ? String(valores.antes ?? '—') : '—'}
                    </td>
                    <td>
                      {valores && typeof valores === 'object'
                        ? String(valores.despues ?? '—')
                        : String(valores)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </article>
  );
};
Movimiento.propTypes = { fila: PropTypes.object };

const SESIONES_VISIBLES = 8;

// Misma estructura que una ficha de movimiento y que una tarjeta de sesión.
const MovimientoEsqueleto = () => (
  <article className="logs-mov logs-mov--esqueleto" aria-hidden="true">
    <div className="logs-mov__cabecera">
      <Esqueleto ancho={120} alto={22} radio={11} />
      <Esqueleto ancho={140} alto={14} />
    </div>
    <div className="logs-mov__campos">
      {[0, 1, 2, 3].map((i) => (
        <div key={i} className="logs-campo">
          <Esqueleto ancho={60} alto={12} />
          <Esqueleto ancho="80%" alto={16} />
          <Esqueleto ancho="60%" alto={13} />
        </div>
      ))}
    </div>
  </article>
);

const SesionEsqueleto = () => (
  <li className="logs-sesion logs-sesion--esqueleto" aria-hidden="true">
    <Esqueleto ancho="55%" alto={16} />
    <Esqueleto ancho="75%" alto={13} />
    <Esqueleto ancho="65%" alto={13} />
    <Esqueleto ancho="85%" alto={13} />
  </li>
);

const Logs = () => {
  const [movimientos, setMovimientos] = useState([]);
  const [tiposEvento, setTiposEvento] = useState([]);
  const [sesiones, setSesiones] = useState([]);
  const [ventanaMinutos, setVentanaMinutos] = useState(15);
  const [cargando, setCargando] = useState(false);
  const [cargandoSesiones, setCargandoSesiones] = useState(false);
  const [verTodas, setVerTodas] = useState(false);

  const [filtroEvento, setFiltroEvento] = useState(null);
  const [filtroTexto, setFiltroTexto] = useState('');
  // El texto vive además en una ref para que teclear no recree cargarMovimientos
  // y, con ello, no dispare el efecto que recarga la tabla en cada pulsación.
  const filtroTextoRef = useRef('');
  const [rangoFechas, setRangoFechas] = useState(null);
  const [paginacion, setPaginacion] = useState({ current: 1, pageSize: 50, total: 0 });

  const cargarMovimientos = useCallback(
    (pagina = 1, tamano = 50) => {
      setCargando(true);

      const params = { page: pagina, per_page: tamano };
      if (filtroEvento) params.event = filtroEvento;
      if (filtroTextoRef.current) params.search = filtroTextoRef.current;
      if (rangoFechas && rangoFechas[0] && rangoFechas[1]) {
        params.from = rangoFechas[0].format('YYYY-MM-DD');
        params.to = rangoFechas[1].format('YYYY-MM-DD');
      }

      clienteAxios
        .get('/logs', { params })
        .then((respuesta) => {
          setMovimientos(respuesta.data.data || []);
          setTiposEvento(respuesta.data.event_types || []);
          const meta = respuesta.data.meta || {};
          setPaginacion({
            current: meta.current_page || 1,
            pageSize: meta.per_page || 50,
            total: meta.total || 0
          });
        })
        .catch((error) => {
          message.error(error.response?.data?.message || 'No se pudo cargar la bitácora.');
        })
        .finally(() => setCargando(false));
    },
    [filtroEvento, rangoFechas]
  );

  const cargarSesiones = useCallback(() => {
    setCargandoSesiones(true);
    clienteAxios
      .get('/logs/sesiones-activas')
      .then((respuesta) => {
        setSesiones(respuesta.data.data || []);
        setVentanaMinutos(respuesta.data.meta?.minutes_window ?? 15);
      })
      .catch((error) => {
        message.error(error.response?.data?.message || 'No se pudieron cargar las sesiones.');
      })
      .finally(() => setCargandoSesiones(false));
  }, []);

  // Depende solo del tipo y del rango: el cuadro de búsqueda no debe recargar
  // en cada tecla, se dispara con Enter o con la lupa.
  useEffect(() => {
    cargarMovimientos(1, paginacion.pageSize);
  }, [filtroEvento, rangoFechas, cargarMovimientos, paginacion.pageSize]);

  useEffect(() => {
    cargarSesiones();
  }, [cargarSesiones]);

  const sesionesMostradas = verTodas ? sesiones : sesiones.slice(0, SESIONES_VISIBLES);

  return (
    <>
      <div className="title-content-between logs-encabezado">
        <h1 className="mb-2 mr-5">Logs</h1>
        <Boton
          type="primary"
          icon={<ReloadOutlined />}
          loading={cargando || cargandoSesiones}
          onClick={() => {
            cargarMovimientos(paginacion.current, paginacion.pageSize);
            cargarSesiones();
          }}>
          Actualizar
        </Boton>
      </div>

      <section className="logs-sesiones" aria-label="Sesiones activas">
        <div className="logs-sesiones__cabecera">
          <h2 className="logs-sesiones__titulo">
            <span className="logs-punto" aria-hidden="true" />
            Con actividad ahora{cargandoSesiones ? '' : ` · ${sesiones.length}`}
          </h2>
          <span className="logs-secundario">Últimos {ventanaMinutos} minutos</span>
        </div>
        <p className="logs-aviso">
          El sistema no guarda sesiones en el servidor, así que esto se deduce de la última petición
          de cada quien: alguien que cerró el navegador sin salir seguirá apareciendo hasta que pase
          ese tiempo.
        </p>
        {cargandoSesiones ? (
          <ul className="logs-sesiones__lista" aria-busy="true">
            <li className="sr-only" role="status">
              Cargando sesiones
            </li>
            {[0, 1, 2, 3].map((i) => (
              <SesionEsqueleto key={i} />
            ))}
          </ul>
        ) : sesiones.length === 0 ? (
          <Empty description="Nadie con actividad reciente" />
        ) : (
          <ul className="logs-sesiones__lista">
            {sesionesMostradas.map((fila) => (
              <li key={fila.id} className="logs-sesion">
                <div className="logs-sesion__nombre">{fila.name}</div>
                <div className="logs-secundario">{fila.email}</div>
                <div className="logs-secundario">
                  {[fila.company, fila.role].filter(Boolean).join(' · ') || '—'}
                </div>
                <div className="logs-secundario">Última actividad {fechaHora(fila.last_login)}</div>
              </li>
            ))}
          </ul>
        )}
        {!cargandoSesiones && sesiones.length > SESIONES_VISIBLES && (
          <button type="button" className="logs-ver-cambios" onClick={() => setVerTodas(!verTodas)}>
            {verTodas ? 'Ver menos' : `Ver las ${sesiones.length} sesiones`}
          </button>
        )}
      </section>

      <section aria-label="Movimientos">
        <div className="logs-herramientas">
          <Select
            allowClear
            placeholder="Todos los tipos"
            aria-label="Filtrar por tipo de movimiento"
            className="logs-filtro"
            value={filtroEvento}
            onChange={setFiltroEvento}
            options={tiposEvento.map((tipo) => ({ value: tipo.value, label: tipo.label }))}
          />
          <RangePicker
            className="logs-filtro"
            value={rangoFechas}
            onChange={setRangoFechas}
            format="DD/MM/YYYY"
          />
          <Input
            className="logs-buscar"
            placeholder="Buscar por persona o descripción"
            aria-label="Buscar movimientos"
            allowClear
            value={filtroTexto}
            onChange={(e) => {
              setFiltroTexto(e.target.value);
              filtroTextoRef.current = e.target.value;
            }}
            onPressEnter={() => cargarMovimientos(1, paginacion.pageSize)}
            suffix={
              <SearchOutlined
                aria-label="Buscar"
                style={{ cursor: 'pointer' }}
                onClick={() => cargarMovimientos(1, paginacion.pageSize)}
              />
            }
          />
        </div>

        <div className="logs-lista" aria-busy={cargando}>
          {cargando ? (
            <>
              <span className="sr-only" role="status">
                Cargando movimientos
              </span>
              {[0, 1, 2, 3].map((i) => (
                <MovimientoEsqueleto key={i} />
              ))}
            </>
          ) : movimientos.length === 0 ? (
            <Empty
              description={
                'Todavía no hay movimientos registrados. La bitácora empieza a' +
                ' guardar desde que se activó: lo anterior no quedó registrado.'
              }
            />
          ) : (
            movimientos.map((fila) => <Movimiento key={fila.id} fila={fila} />)
          )}
        </div>

        <Pagination
          className="logs-paginacion mt-3 mb-4"
          current={paginacion.current}
          pageSize={paginacion.pageSize}
          total={paginacion.total}
          showSizeChanger
          pageSizeOptions={['25', '50', '100', '200']}
          showTotal={(total) => `${total} movimientos`}
          onChange={(pagina, tamano) => cargarMovimientos(pagina, tamano)}
        />
      </section>
    </>
  );
};

export default Logs;
